/**
 * The input-mask presets offered on a Text Input field, and the date-default sentinels.
 *
 * Defined once. The patterns are raw `ngx-mask` patterns, and deliberately the **same strings** the
 * backend already ships for built-in fields through `@BuiltInField(mask = …)` —
 * `Eviction.propertyZipCode` is `00000`, `Tenant.phoneNumber` is `(000) 000-0000`, `Tenant.ssn` is
 * `000-00-0000` — which the eviction intake sections also hardcode inline. A custom field's mask and a
 * built-in field's mask are therefore the same kind of thing, needing no translation between them.
 *
 * **A mask is not cosmetic.** `provideEnvironmentNgxMask()` is called with no configuration, so
 * ngx-mask runs at its default `dropSpecialCharacters: true` and the mask characters are stripped from
 * the model value. That matches the built-in fields, whose stored values are already unformatted
 * (production holds org phone numbers as ten bare digits and zips as five). The consequence to disclose
 * in the UI is that adding a mask to a populated field changes the shape of every value saved
 * afterwards, while leaving the existing ones alone.
 */
export interface MaskPreset {
  /** What the picker shows. */
  label: string;
  /** The raw ngx-mask pattern, or null for an unrestricted input. */
  pattern: string | null;
}

export const MASK_PRESETS: MaskPreset[] = [
  { label: 'None', pattern: null },
  { label: 'Numbers only', pattern: '0*' },
  { label: 'Phone', pattern: '(000) 000-0000' },
  { label: 'SSN', pattern: '000-00-0000' },
  { label: 'ZIP code', pattern: '00000' },
  { label: 'ZIP+4', pattern: '00000-0000' },
  { label: 'EIN', pattern: '00-0000000' }
];

/** The picker label for a stored pattern; falls back to the raw pattern for anything unrecognised. */
export function maskPresetLabel(pattern: string | null | undefined): string {
  if (!pattern) {
    return 'None';
  }
  return MASK_PRESETS.find(p => p.pattern === pattern)?.label ?? pattern;
}

/**
 * A DATE field's `default_value` sentinel meaning "today when the form is built", rather than a fixed
 * calendar date. Resolved at form construction, so it cannot go stale.
 */
export const DATE_DEFAULT_TODAY = 'TODAY';

/**
 * A DATE field's `default_value` sentinel meaning "always open empty, ignoring the org date-default
 * settings" — the blank-but-required case.
 *
 * Distinct from `default_value` being **absent**, which means "inherit those settings". It has to be a
 * sentinel rather than null because the org settings default to *on*: a single ambiguous "None" choice
 * would mean "today" for precisely the orgs that asked to be able to leave a required date blank.
 */
export const DATE_DEFAULT_BLANK = 'BLANK';

/** The four states a DATE field's default can be in, as offered by the editor's picker. */
export type DateDefaultMode = 'inherit' | 'blank' | 'today' | 'fixed';

/** Which of the four states a stored `default_value` represents. */
export function dateDefaultMode(value: unknown): DateDefaultMode {
  if (value === DATE_DEFAULT_TODAY) {
    return 'today';
  }
  if (value === DATE_DEFAULT_BLANK) {
    return 'blank';
  }
  // Absent — and an empty string, which is what a cleared text control leaves behind — is "inherit".
  return value === null || value === undefined || value === '' ? 'inherit' : 'fixed';
}
